/* ══════════════════════════════════════════════════════════════
   Indian Number Formatting — ₹ Lakh / Crore System
   ══════════════════════════════════════════════════════════════ */

const CRORE = 10_000_000;
const LAKH = 100_000;
const THOUSAND = 1_000;

/**
 * Format a number using the Indian numbering system (Lakh/Crore)
 * @param value — Numeric value
 * @param options — Formatting options
 * @returns Formatted string like "₹2.30 Lakh Cr" or "₹45,200"
 */
export function formatIndianNumber(
    value: number,
    options: {
        currency?: boolean;
        decimals?: number;
        compact?: boolean;
        suffix?: string;
    } = {}
): string {
    const { currency = false, decimals = 2, compact = true, suffix = '' } = options;
    const prefix = currency ? '₹' : '';
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';

    if (compact) {
        if (absValue >= CRORE * CRORE) {
            // Lakh Crore range (₹ in Lakh Crores)
            return `${sign}${prefix}${(absValue / (CRORE * LAKH)).toFixed(decimals)} Lakh Cr${suffix}`;
        }
        if (absValue >= CRORE * 100) {
            // Thousand Crore range
            return `${sign}${prefix}${(absValue / (CRORE * 100)).toFixed(decimals)}K Cr${suffix}`;
        }
        if (absValue >= CRORE) {
            return `${sign}${prefix}${(absValue / CRORE).toFixed(decimals)} Cr${suffix}`;
        }
        if (absValue >= LAKH) {
            return `${sign}${prefix}${(absValue / LAKH).toFixed(decimals)} L${suffix}`;
        }
        if (absValue >= THOUSAND) {
            return `${sign}${prefix}${(absValue / THOUSAND).toFixed(decimals)}K${suffix}`;
        }
    }

    // Indian comma-separated format: 1,23,45,678
    return `${sign}${prefix}${formatWithIndianCommas(absValue, decimals)}${suffix}`;
}

/**
 * Format number with Indian comma system (XX,XX,XX,XXX)
 */
export function formatWithIndianCommas(value: number, decimals: number = 0): string {
    const [intPart, decPart] = value.toFixed(decimals).split('.');
    const lastThree = intPart.slice(-3);
    const remaining = intPart.slice(0, -3);

    const formattedRemaining = remaining
        ? remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ','
        : '';

    return formattedRemaining + lastThree + (decPart ? `.${decPart}` : '');
}

/**
 * Format FII/DII flow value
 * @param crores — Value in crores (can be negative)
 * @returns "+₹2,300 Cr" or "-₹500 Cr"
 */
export function formatFlow(crores: number): string {
    const sign = crores >= 0 ? '+' : '';
    return `${sign}₹${formatWithIndianCommas(Math.abs(crores), 0)} Cr`;
}

/**
 * Format percentage change
 * @param pct — Percentage value
 * @returns "+1.85%" or "-0.42%"
 */
export function formatPercent(pct: number, decimals: number = 2): string {
    const sign = pct >= 0 ? '+' : '';
    return `${sign}${pct.toFixed(decimals)}%`;
}

/**
 * Format Nifty/Sensex points change
 * @param points — Points change
 * @returns "+185.50" or "-42.30"
 */
export function formatPoints(points: number, decimals: number = 2): string {
    const sign = points >= 0 ? '+' : '';
    return `${sign}${points.toFixed(decimals)}`;
}

/**
 * Format market price (no compaction)
 * @param price — Price value
 * @returns "23,150.45"
 */
export function formatPrice(price: number, decimals: number = 2): string {
    return formatWithIndianCommas(price, decimals);
}
